import React, { useState, useEffect } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import { ROOTS_ENDPOINT } from '../config/api';
import { SHARED_CHART_COLORS } from '../constants/chartColors';
import { getChartCache, setChartCache, CHART_KEYS } from '../utils/cache';
import './RootsChart.css';

const MOBILE_BREAKPOINT = 768;

/** Wraps X-axis label text to multiple lines so it stays horizontal. isNarrowViewport is hoisted to the
 * parent chart (single matchMedia listener) instead of one per tick, since Recharts renders one tick per bar. */
function RootsXAxisTick({ x, y, payload, isNarrowViewport }) {
  const text = toProperCase(payload?.value ?? '');
  const tickWidth = isNarrowViewport ? 52 : 88;
  const fontSize = isNarrowViewport ? 9 : 11;
  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-tickWidth / 2} y={0} width={tickWidth} height={isNarrowViewport ? 28 : 32} style={{ overflow: 'visible' }}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          className="roots-chart-xaxis-tick"
          style={{
            width: tickWidth,
            fontSize,
            fontWeight: 600,
            color: '#374151',
            textAlign: 'center',
            wordBreak: 'break-word',
            lineHeight: 1.2,
            overflow: 'visible',
          }}
        >
          {text}
        </div>
      </foreignObject>
    </g>
  );
}

function toProperCase(str) {
  return (str || '').trim().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build bar chart data from API rows: one bar per (root_category, root_name), value = session_count.
 * Label = "root_name" with category in tooltip; sorted by session_count desc.
 */
function buildBarData(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const aggregated = new Map();
  for (const r of rows) {
    const cat = (r.root_category || '').trim().toLowerCase();
    const name = typeof r.root_name === 'string' ? (r.root_name || '').trim() : '';
    if (!cat || !name || cat === 'other' || name.toLowerCase() === 'other') continue;
    const key = `${cat}\0${name}`;
    aggregated.set(key, (aggregated.get(key) || 0) + (Number(r.session_count) || 0));
  }

  const bars = [];
  for (const [key, value] of aggregated) {
    const [category, name] = key.split('\0');
    bars.push({
      category,
      name,
      label: name,
      sessions: value,
    });
  }
  bars.sort((a, b) => b.sessions - a.sessions);
  return bars;
}

/** Sample bar data for new users: same shape as buildBarData output ({ category, name, label, sessions }). */
function getSampleRootsBarData() {
  return [
    { category: 'family', name: 'Family dynamics', label: 'Family dynamics', sessions: 3 },
    { category: 'family', name: 'Childhood experiences', label: 'Childhood experiences', sessions: 2 },
    { category: 'work', name: 'Career expectations', label: 'Career expectations', sessions: 2 },
    { category: 'relationships', name: 'Attachment patterns', label: 'Attachment patterns', sessions: 1 },
    { category: 'identity', name: 'Self-worth', label: 'Self-worth', sessions: 1 },
  ];
}

function RootsChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsNarrowViewport(mq.matches);
    handler();
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const user = await getCurrentUser();
        const uid = user?.userId;
        const cached = uid ? getChartCache(CHART_KEYS.roots, uid) : null;
        if (!cancelled && cached != null && Array.isArray(cached) && cached.length > 0) {
          setData(cached);
          setLoading(false);
        }
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const res = await fetch(ROOTS_ENDPOINT, {
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to load roots');
        const json = await res.json();
        const payload = json?.statusCode != null ? JSON.parse(json.body || '[]') : json;
        const list = Array.isArray(payload) ? payload : [];
        if (!cancelled) {
          setData(list);
          setError(null);
          if (uid) setChartCache(CHART_KEYS.roots, uid, list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading roots:', err);
          setError(err.message);
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="roots-chart-container">
        <div className="roots-chart-loading">Loading roots...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="roots-chart-container">
        <div className="roots-chart-error">Unable to load roots</div>
      </div>
    );
  }

  const barDataFromApi = buildBarData(data || []).slice(0, 7);
  const hasRealData = barDataFromApi.length > 0;
  const barData = hasRealData ? barDataFromApi : getSampleRootsBarData();

  return (
    <div className="roots-chart-container">
      {!hasRealData && (
        <div className="chart-cta-new-user" role="status">
          <p className="chart-cta-new-user-headline">Start journalling to see your data.</p>
          <p className="chart-cta-new-user-note">This is a sample view.</p>
        </div>
      )}
      <div className="roots-chart-body">
        <ResponsiveContainer width="100%" height={320} minWidth={320}>
          <BarChart
            data={barData}
            margin={{ top: 16, right: 24, bottom: 44, left: 36 }}
          >
            <XAxis
              type="category"
              dataKey="name"
              tick={<RootsXAxisTick isNarrowViewport={isNarrowViewport} />}
              axisLine={{ stroke: '#9ca3af', strokeWidth: 1.5 }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              type="number"
              allowDecimals={false}
              tick={false}
              axisLine={{ stroke: '#9ca3af', strokeWidth: 1.5 }}
              tickLine={false}
              label={{
                value: 'Number of sessions',
                angle: -90,
                position: 'center',
                offset: -8,
                style: { fontSize: 14, fill: '#111827', fontWeight: 600 },
              }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const d = payload[0].payload;
                return (
                  <div className="roots-chart-tooltip">
                    <div className="roots-chart-tooltip-label">{toProperCase(d.name)}</div>
                    <div className="roots-chart-tooltip-meta">{toProperCase(d.category)}</div>
                    <div className="roots-chart-tooltip-value">
                      {d.sessions} session{d.sessions === 1 ? '' : 's'}
                    </div>
                  </div>
                );
              }}
            />
            <Bar dataKey="sessions" radius={[4, 4, 0, 0]} maxBarSize={48}>
              <LabelList
                dataKey="sessions"
                position="top"
                offset={6}
                style={{ fontSize: 11, fill: '#6b7280' }}
              />
              {barData.map((_, i) => (
                <Cell key={i} fill={SHARED_CHART_COLORS[i % SHARED_CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default RootsChart;
